/**
 * MINIMA - Native Built-in Wishlist Engine
 * Fast, lightweight, zero-dependency client-side wishlist system using localStorage & Shopify Ajax API.
 */

class MinimaWishlist {
  constructor() {
    this.STORAGE_KEY = 'minima_wishlist_items';
    this.items = this.getStorage();
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateUI();
    this.renderDrawer();
    this.renderPageGrid();
    this.validateItems(); // Check against Shopify to auto-prune deleted products

    // Re-bind when Shopify re-renders dynamic sections
    document.addEventListener('shopify:section:load', () => {
      this.updateUI();
      this.renderPageGrid();
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.renderPageGrid();
        this.updateUI();
        this.validateItems();
      });
    }
  }

  getStorage() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      // Sanitize: filter out invalid entries lacking both handle and id
      const seen = new Set();
      return parsed.filter(item => {
        if (!item || typeof item !== 'object') return false;
        const key = (item.handle || item.id || '').trim();
        if (!key) return false;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    } catch (e) {
      console.warn('[MINIMA Wishlist] localStorage unavailable:', e);
      return [];
    }
  }

  setStorage(items) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn('[MINIMA Wishlist] Failed to save to localStorage:', e);
    }
    this.items = items;
    this.updateUI();
    this.renderDrawer();
    this.renderPageGrid();
    window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items: this.items } }));
  }

  /**
   * Check if an item exists in wishlist by handle, id, or variantId
   */
  isInWishlist(identifier) {
    if (!identifier) return false;
    const key = String(identifier).trim().toLowerCase();
    return this.items.some(item => {
      if (!item) return false;
      const h = item.handle ? String(item.handle).trim().toLowerCase() : '';
      const id = item.id ? String(item.id).trim().toLowerCase() : '';
      const vid = item.variantId ? String(item.variantId).trim().toLowerCase() : '';
      return h === key || id === key || vid === key;
    });
  }

  toggle(productData) {
    if (!productData) return;
    const key = productData.handle || productData.id;
    if (!key) return;

    const exists = this.isInWishlist(key);
    if (exists) {
      this.remove(key);
    } else {
      this.add(productData);
    }
  }

  toggleFromButton(btn) {
    if (!btn) return;

    // Prevent double-execution from duplicate click/inline handlers
    const now = Date.now();
    if (btn._lastWishlistToggle && now - btn._lastWishlistToggle < 300) {
      return;
    }
    btn._lastWishlistToggle = now;

    const productData = {
      handle: btn.getAttribute('data-wishlist-handle'),
      id: btn.getAttribute('data-wishlist-id'),
      variantId: btn.getAttribute('data-wishlist-variant-id') || btn.getAttribute('data-wishlist-id'),
      title: btn.getAttribute('data-wishlist-title'),
      price: btn.getAttribute('data-wishlist-price'),
      image: btn.getAttribute('data-wishlist-image'),
      url: btn.getAttribute('data-wishlist-url'),
      available: btn.getAttribute('data-wishlist-available') !== 'false'
    };
    if (productData.handle || productData.id) {
      this.toggle(productData);
    }
  }

  add(productData) {
    if (!productData) return;
    const handle = productData.handle ? String(productData.handle).trim() : '';
    const id = productData.id ? String(productData.id).trim() : '';
    if (!handle && !id) return;

    if (!this.isInWishlist(handle) && !this.isInWishlist(id)) {
      const cleanItem = {
        handle: handle,
        id: id || handle,
        variantId: productData.variantId ? String(productData.variantId).trim() : (id || handle),
        title: productData.title ? String(productData.title).trim() : 'Product',
        price: productData.price || '',
        image: productData.image || '',
        url: productData.url || (handle ? `/products/${handle}` : '#'),
        available: productData.available !== false && productData.available !== 'false'
      };
      const updated = [...this.items, cleanItem];
      this.setStorage(updated);
    }
  }

  remove(identifier) {
    if (!identifier) {
      // Purge any corrupted or empty items
      const updated = this.items.filter(item => item && (item.handle || item.id));
      this.setStorage(updated);
      return;
    }
    const key = String(identifier).trim().toLowerCase();
    const updated = this.items.filter(item => {
      if (!item) return false;
      const h = item.handle ? String(item.handle).trim().toLowerCase() : '';
      const id = item.id ? String(item.id).trim().toLowerCase() : '';
      const vid = item.variantId ? String(item.variantId).trim().toLowerCase() : '';
      return h !== key && id !== key && vid !== key;
    });
    this.setStorage(updated);
  }

  clear() {
    this.setStorage([]);
  }

  /**
   * Automatically validates wishlist items against Shopify Ajax API.
   * If a product was deleted in Shopify Admin (404), purges it automatically.
   */
  async validateItems() {
    if (!this.items || this.items.length === 0) return;

    try {
      const checks = this.items.map(async (item) => {
        if (!item.handle) return { item, deleted: true };
        try {
          const res = await fetch(`/products/${encodeURIComponent(item.handle)}.js`, { method: 'GET' });
          if (res.status === 404) {
            return { item, deleted: true };
          }
          return { item, deleted: false };
        } catch (e) {
          // Keep item on network drop
          return { item, deleted: false };
        }
      });

      const results = await Promise.all(checks);
      const deletedItems = results.filter(r => r.deleted);
      if (deletedItems.length > 0) {
        console.info('[MINIMA Wishlist] Auto-pruning deleted products:', deletedItems.map(d => d.item.handle || d.item.id));
        const deletedHandles = new Set(deletedItems.map(d => (d.item.handle || '').toLowerCase()).filter(Boolean));
        const deletedIds = new Set(deletedItems.map(d => (d.item.id || '').toLowerCase()).filter(Boolean));
        const cleaned = this.items.filter(item => {
          if (!item.handle && !item.id) return false;
          const h = (item.handle || '').toLowerCase();
          const id = (item.id || '').toLowerCase();
          if (h && deletedHandles.has(h)) return false;
          if (id && deletedIds.has(id)) return false;
          return true;
        });
        this.setStorage(cleaned);
      }
    } catch (err) {
      console.warn('[MINIMA Wishlist] Validation skipped:', err);
    }
  }

  updateUI() {
    const count = this.items.length;

    // Update all count badges
    document.querySelectorAll('.wishlist-count').forEach(el => {
      el.textContent = count;
    });

    document.querySelectorAll('.wishlist-count-bubble').forEach(el => {
      if (count > 0) {
        el.style.display = 'flex';
        el.classList.remove('is-updated');
        void el.offsetWidth; // trigger reflow
        el.classList.add('is-updated');
      } else {
        el.style.display = 'none';
      }
    });

    // Update card buttons on collection/catalog/home pages (exclude wishlist page cards)
    document.querySelectorAll('.card__wishlist-btn:not(#MinimaWishlistPageGrid .card__wishlist-btn)').forEach(btn => {
      const handle = btn.getAttribute('data-wishlist-handle');
      const id = btn.getAttribute('data-wishlist-id');
      if ((handle && this.isInWishlist(handle)) || (id && this.isInWishlist(id))) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-label', 'Remove from Wishlist');
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-label', 'Add to Wishlist');
      }
    });

    // Update PDP buttons
    document.querySelectorAll('.product__wishlist-btn').forEach(btn => {
      const handle = btn.getAttribute('data-wishlist-handle');
      const id = btn.getAttribute('data-wishlist-id');
      const textSpan = btn.querySelector('.product__wishlist-btn-text');
      if ((handle && this.isInWishlist(handle)) || (id && this.isInWishlist(id))) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-label', 'Remove from Wishlist');
        if (textSpan) textSpan.textContent = 'Saved in Wishlist';
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-label', 'Add to Wishlist');
        if (textSpan) textSpan.textContent = 'Add to Wishlist';
      }
    });

    // Update Wishlist page clear all button visibility
    const clearBtn = document.querySelector('#MinimaWishlistClearAll');
    if (clearBtn) {
      clearBtn.style.display = count > 0 ? 'inline-block' : 'none';
    }
  }

  renderDrawer() {
    const drawerContainer = document.querySelector('.wishlist-drawer__content');
    const countHeader = document.querySelector('.wishlist-drawer__count');
    if (!drawerContainer) return;

    if (countHeader) {
      countHeader.textContent = `(${this.items.length})`;
    }

    if (this.items.length === 0) {
      drawerContainer.innerHTML = `
        <div class="wishlist-drawer__empty">
          <h3 class="wishlist-drawer__empty-title">Your wishlist is empty</h3>
          <p class="wishlist-drawer__empty-text">Explore our collections to save your favorite statement pieces.</p>
          <a href="/collections/all" class="wishlist-drawer__empty-btn" onclick="window.minimaWishlist && window.minimaWishlist.closeDrawer()">Explore Catalog</a>
        </div>
      `;
      return;
    }

    let itemsHtml = '<ul class="wishlist-drawer__items">';
    this.items.forEach(item => {
      const isAvailable = item.available !== 'false' && item.available !== false;
      const itemHandle = this.escapeHtml(item.handle || '');
      const itemId = this.escapeHtml(item.id || item.variantId || '');
      const itemTitle = this.escapeHtml(item.title || 'Product');
      const itemUrl = item.url ? this.escapeHtml(item.url) : (item.handle ? `/products/${encodeURIComponent(item.handle)}` : '#');
      let imgUrl = item.image || '';
      if (imgUrl && imgUrl.startsWith('//')) {
        imgUrl = 'https:' + imgUrl;
      }

      itemsHtml += `
        <li class="wishlist-item" data-wishlist-item="${itemHandle}">
          <a href="${itemUrl}" class="wishlist-item__image-wrapper" tabindex="-1">
            ${imgUrl ? `<img src="${imgUrl}" alt="${itemTitle}" class="wishlist-item__image" loading="lazy" width="84" height="112">` : `<div style="width: 84px; height: 112px; background: rgba(0,0,0,0.05); display: flex; align-items: center; justify-content: center; color: #999; font-size: 1rem;">No Image</div>`}
          </a>
          <div class="wishlist-item__details">
            <div>
              <div class="wishlist-item__header">
                <a href="${itemUrl}" class="wishlist-item__title">${itemTitle}</a>
                <button 
                  type="button" 
                  class="wishlist-item__remove" 
                  aria-label="Remove item" 
                  data-wishlist-action="remove" 
                  data-wishlist-handle="${itemHandle}" 
                  data-wishlist-id="${itemId}"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </button>
              </div>
              <div class="wishlist-item__price">${item.price || ''}</div>
            </div>
            <div>
              <button 
                type="button" 
                class="wishlist-item__add-btn" 
                data-variant-id="${item.variantId || item.id}" 
                ${!isAvailable ? 'disabled' : ''}
                onclick="window.minimaWishlist && window.minimaWishlist.addToBag(this, '${item.variantId || item.id}', '${itemHandle}')"
              >
                ${isAvailable ? 'Add to Bag' : 'Sold Out'}
              </button>
            </div>
          </div>
        </li>
      `;
    });
    itemsHtml += '</ul>';

    drawerContainer.innerHTML = itemsHtml;
  }

  renderPageGrid() {
    const pageGrid = document.querySelector('#MinimaWishlistPageGrid');
    const emptyState = document.querySelector('#MinimaWishlistPageEmpty');
    const countText = document.querySelector('#MinimaWishlistPageCount');
    if (!pageGrid && !emptyState) return;

    if (countText) {
      countText.textContent = `${this.items.length} ${this.items.length === 1 ? 'item' : 'items'}`;
    }

    if (this.items.length === 0) {
      if (pageGrid) pageGrid.style.setProperty('display', 'none', 'important');
      if (emptyState) emptyState.style.setProperty('display', 'flex', 'important');
      return;
    }

    if (emptyState) emptyState.style.setProperty('display', 'none', 'important');
    if (pageGrid) {
      pageGrid.style.setProperty('display', 'grid', 'important');

      let gridHtml = '';
      this.items.forEach(item => {
        const isAvailable = item.available !== 'false' && item.available !== false;
        let imgUrl = item.image || '';
        if (imgUrl && imgUrl.startsWith('//')) {
          imgUrl = 'https:' + imgUrl;
        }
        const itemHandle = this.escapeHtml(item.handle || '');
        const itemId = this.escapeHtml(item.id || item.variantId || '');
        const itemTitle = this.escapeHtml(item.title || 'Product');
        const itemUrl = item.url ? this.escapeHtml(item.url) : (item.handle ? `/products/${encodeURIComponent(item.handle)}` : '#');

        const itemPrice = this.escapeHtml(String(item.price || '').replace(/\s+[A-Z]{3}$/, '').replace(/^(\D*\d[\d,]*)$/, '$1.00'));
        const variantId = this.escapeHtml(String(item.variantId || item.id || ''));

        gridHtml += `
          <div class="grid__item wishlist-page-card" data-wishlist-item="${itemHandle}">
            <div class="card-wrapper product-card-wrapper" style="position: relative;">
              <button
                type="button"
                class="card__wishlist-btn is-active"
                aria-label="Remove from Wishlist"
                data-wishlist-action="remove"
                data-wishlist-handle="${itemHandle}"
                data-wishlist-id="${itemId}"
              >
                <svg class="icon icon-heart" viewBox="0 0 24 24" fill="#000000" stroke="#000000" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 21 C7.5 17.5 3.5 13.8 3.5 9 C3.5 6.2 5.7 4 8.5 4 C10.2 4 11.4 4.8 12 6 C12.6 4.8 13.8 4 15.5 4 C18.3 4 20.5 6.2 20.5 9 C20.5 13.8 16.5 17.5 12 21 Z"/>
                </svg>
              </button>
              <div class="card card--standard card--media">
                <div class="card__inner ratio" style="--ratio-percent: 150%;">
                  <a href="${itemUrl}" class="card__media wishlist-page-card__media" aria-label="${itemTitle}">
                    ${imgUrl ? `<img src="${this.escapeHtml(imgUrl)}" alt="${itemTitle}" loading="lazy">` : ''}
                  </a>
                  ${isAvailable ? `
                  <button
                    type="button"
                    class="card__quick-add-btn"
                    aria-label="Quick Add ${itemTitle}"
                    onclick="event.preventDefault(); event.stopPropagation(); window.minimaWishlist && window.minimaWishlist.addToBag(this, '${variantId}', '${itemHandle}');"
                  >
                    <svg class="icon icon-plus" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </button>` : ''}
                </div>
                <div class="card__content">
                  <div class="card__information">
                    <h3 class="card__heading h5">
                      <a href="${itemUrl}" class="full-unstyled-link">${itemTitle}</a>
                    </h3>
                    <div class="card-information">
                      <div class="price"><span class="price-item price-item--regular">${isAvailable ? itemPrice : 'Sold out'}</span></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;
      });

      pageGrid.innerHTML = gridHtml;
    }
  }

  async addToBag(btnElem, variantId, handle) {
    if (!variantId) return;

    const isIconBtn = btnElem.classList.contains('card__quick-add-btn') || !!btnElem.querySelector('svg');
    const originalHTML = btnElem.innerHTML;
    const originalText = btnElem.textContent;
    btnElem.disabled = true;

    if (isIconBtn) {
      btnElem.style.opacity = '0.6';
    } else {
      btnElem.textContent = 'Adding...';
    }

    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: ['cart-drawer', 'cart-icon-bubble'].join(',')
        })
      });

      if (!response.ok) throw new Error('Add to cart failed');
      const responseData = await response.json();

      if (isIconBtn) {
        btnElem.style.opacity = '1';
        btnElem.innerHTML = `<svg class="icon icon-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width: 1.6rem; height: 1.6rem;"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
      } else {
        btnElem.textContent = 'Added ✓';
      }

      setTimeout(() => {
        btnElem.disabled = false;
        if (isIconBtn) {
          btnElem.innerHTML = originalHTML;
        } else {
          btnElem.textContent = originalText;
        }
      }, 1500);

      // Refresh cart data
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      const count = cartData.item_count;

      // Update all cart count bubbles in DOM
      const bubbles = document.querySelectorAll('.cart-count-bubble, #cart-icon-bubble .cart-count-bubble');
      if (bubbles.length > 0) {
        bubbles.forEach(el => {
          if (count > 0) {
            el.style.display = 'flex';
            let span = el.querySelector('span[aria-hidden="true"]') || el.querySelector('span');
            if (span) span.textContent = count;
          } else {
            el.style.display = 'none';
          }
        });
      }

      // If cart count bubble is missing in header, dynamically append it
      const cartIcon = document.querySelector('#cart-icon-bubble');
      if (cartIcon && !cartIcon.querySelector('.cart-count-bubble') && count > 0) {
        const newBubble = document.createElement('div');
        newBubble.className = 'cart-count-bubble';
        newBubble.style.display = 'flex';
        newBubble.innerHTML = `<span aria-hidden="true">${count}</span>`;
        cartIcon.appendChild(newBubble);
      }

      // If Cart Drawer exists, render contents and open it
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        this.closeDrawer();
        if (typeof cartDrawer.renderContents === 'function' && responseData.sections) {
          cartDrawer.renderContents(responseData);
        } else if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      }
    } catch (err) {
      console.error('[MINIMA Wishlist] Error adding to cart:', err);
      btnElem.disabled = false;
      if (isIconBtn) {
        btnElem.style.opacity = '1';
        btnElem.innerHTML = originalHTML;
      } else {
        btnElem.textContent = 'Unavailable';
        setTimeout(() => {
          btnElem.textContent = originalText;
        }, 2000);
      }
    }
  }

  openDrawer() {
    const drawer = document.querySelector('.wishlist-drawer');
    const overlay = document.querySelector('.wishlist-drawer-overlay');
    if (drawer && overlay) {
      this.renderDrawer();
      overlay.classList.add('is-open');
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
  }

  closeDrawer() {
    const drawer = document.querySelector('.wishlist-drawer');
    const overlay = document.querySelector('.wishlist-drawer-overlay');
    if (drawer && overlay) {
      overlay.classList.remove('is-open');
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }

  bindEvents() {
    const handleWishlistClick = (e) => {
      // 1. Explicit remove buttons (drawer remove icon or wishlist page remove buttons)
      const removeBtn = e.target.closest('[data-wishlist-action="remove"], .wishlist-item__remove, .wishlist-page-remove-btn');
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        const handle = removeBtn.getAttribute('data-wishlist-handle') || removeBtn.getAttribute('data-wishlist-id');
        this.remove(handle);
        return;
      }

      // 2. Wishlist page heart button (clicking it removes item from wishlist)
      const pageCardBtn = e.target.closest('#MinimaWishlistPageGrid .card__wishlist-btn');
      if (pageCardBtn) {
        e.preventDefault();
        e.stopPropagation();
        const handle = pageCardBtn.getAttribute('data-wishlist-handle') || pageCardBtn.getAttribute('data-wishlist-id');
        this.remove(handle);
        return;
      }

      // 3. Product card wishlist toggle button (on collection, search, home pages)
      const cardBtn = e.target.closest('.card__wishlist-btn');
      if (cardBtn) {
        e.preventDefault();
        e.stopPropagation();

        const now = Date.now();
        if (cardBtn._lastWishlistToggle && now - cardBtn._lastWishlistToggle < 300) {
          return;
        }
        cardBtn._lastWishlistToggle = now;

        const productData = {
          handle: cardBtn.getAttribute('data-wishlist-handle'),
          id: cardBtn.getAttribute('data-wishlist-id'),
          variantId: cardBtn.getAttribute('data-wishlist-variant-id') || cardBtn.getAttribute('data-wishlist-id'),
          title: cardBtn.getAttribute('data-wishlist-title'),
          price: cardBtn.getAttribute('data-wishlist-price'),
          image: cardBtn.getAttribute('data-wishlist-image'),
          url: cardBtn.getAttribute('data-wishlist-url'),
          available: cardBtn.getAttribute('data-wishlist-available') !== 'false'
        };

        if (productData.handle || productData.id) {
          this.toggle(productData);
        }
        return;
      }

      // 4. Product Detail Page (PDP) wishlist button
      const pdpBtn = e.target.closest('.product__wishlist-btn');
      if (pdpBtn) {
        e.preventDefault();
        e.stopPropagation();

        const now = Date.now();
        if (pdpBtn._lastWishlistToggle && now - pdpBtn._lastWishlistToggle < 300) {
          return;
        }
        pdpBtn._lastWishlistToggle = now;

        const productData = {
          handle: pdpBtn.getAttribute('data-wishlist-handle'),
          id: pdpBtn.getAttribute('data-wishlist-id'),
          variantId: pdpBtn.getAttribute('data-wishlist-variant-id') || pdpBtn.getAttribute('data-wishlist-id'),
          title: pdpBtn.getAttribute('data-wishlist-title'),
          price: pdpBtn.getAttribute('data-wishlist-price'),
          image: pdpBtn.getAttribute('data-wishlist-image'),
          url: pdpBtn.getAttribute('data-wishlist-url'),
          available: pdpBtn.getAttribute('data-wishlist-available') !== 'false'
        };

        if (productData.handle || productData.id) {
          this.toggle(productData);
        }
        return;
      }

      // 5. Close drawer button or backdrop click (if drawer is present)
      if (e.target.closest('.wishlist-drawer__close') || e.target.classList.contains('wishlist-drawer-overlay')) {
        e.preventDefault();
        e.stopPropagation();
        this.closeDrawer();
        return;
      }
    };

    // Use capturing phase so our listener runs before card link navigation
    document.addEventListener('click', handleWishlistClick, true);

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeDrawer();
      }
    });
  }

  escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

// Auto-instantiate on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.minimaWishlist = new MinimaWishlist();
  });
} else {
  window.minimaWishlist = new MinimaWishlist();
}
